/**
 * <collection-tabs> — client-side tab filtering for a grid of collection cards.
 *
 * Cards are server-rendered (good for SEO/perf); this only toggles visibility.
 * Tab buttons carry data-tab-target ("all" or a group handle); cards carry
 * data-tab (their group handle). Clicking a tab shows the matching cards.
 */
if (!customElements.get('collection-tabs')) {
  class CollectionTabs extends HTMLElement {
    connectedCallback() {
      this.tabButtons = Array.from(this.querySelectorAll('[data-tab-target]'));
      this.cards = Array.from(this.querySelectorAll('[data-tab]'));
      if (this.tabButtons.length === 0) return;

      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);

      const initial =
        this.tabButtons.find((b) => b.classList.contains('is-active')) || this.tabButtons[0];
      this.activate(initial, false);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    onClick(event) {
      const button = event.target.closest('[data-tab-target]');
      if (button && this.contains(button)) {
        event.preventDefault();
        this.activate(button);
      }
    }

    activate(button, focus = true) {
      if (!button) return;
      const target = button.dataset.tabTarget;

      this.tabButtons.forEach((b) => {
        const active = b === button;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-selected', active ? 'true' : 'false');
        b.tabIndex = active ? 0 : -1;
      });

      this.cards.forEach((card) => {
        const show = target === 'all' || card.dataset.tab === target;
        card.toggleAttribute('hidden', !show);
        // If a shown panel contains carousels, (re)mount them now that they
        // finally have layout — Splide can't measure inside a hidden panel.
        if (show) {
          card.querySelectorAll('splide-carousel').forEach((carousel) => {
            if (typeof carousel.ensureMounted === 'function') carousel.ensureMounted();
          });
        }
      });

      if (focus) button.focus();
    }

    /* Theme editor: when a collection block is selected, reveal it. */
    revealCard(blockId) {
      const card = this.querySelector(`#${CSS.escape(blockId)}`);
      if (!card) return;
      const group = card.dataset.tab;
      const tab =
        this.tabButtons.find((b) => b.dataset.tabTarget === group) ||
        this.tabButtons.find((b) => b.dataset.tabTarget === 'all');
      this.activate(tab, false);
    }
  }

  customElements.define('collection-tabs', CollectionTabs);

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:block:select', (event) => {
      const el = event.target.closest('collection-tabs');
      if (el) el.revealCard(event.detail.blockId);
    });
  }
}
